import { useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { cloneDeep, find, findIndex } from 'lodash';

import { useMenuService } from '@/data';
import { useSpecialsContext } from '@/context';
import { useData } from '@/components';
import { useProducts, useSelectedStore } from '@/hooks';
import {
  CartLineItem,
  GProduct,
  ISpecial,
  PromoCarouselDocumentDataCarouselItemsItem,
} from '@/types';

import { formatSpecial } from './utils';

interface IUseBundleSpecial {
  id?: string;
}

interface IUseBunbleSpecialResponse {
  bundleProducts: CartLineItem[];
  currentBundleProducts: CartLineItem[];
  dependentProducts: CartLineItem[];
  independentProducts: CartLineItem[];
  initDepProducts: GProduct[];
  initIndProducts: GProduct[];
  isLoading: boolean;
  processingDependents: boolean;
  processingIndependents: boolean;
  special?: ISpecial;
}

const useBundleSpecial = ({
  id,
}: IUseBundleSpecial): IUseBunbleSpecialResponse => {
  const { store } = useSelectedStore();
  const { products, update } = useProducts();
  const { isLoading, specials } = useSpecialsContext();
  const { searchProductsByGroupId } = useMenuService();
  const { promoCarousel } = useData();

  const accounting = products?.reduce((result, current) => {
    return result + current?.quantity;
  }, 0);

  const currentSpecial = specials?.find(sp => sp?.id === id);
  const currentBundleProducts =
    products?.filter(prod => prod?.bundle_id === currentSpecial?.id) ?? [];
  const dependentGroupIds = currentSpecial?.groupsRequired ?? [];
  const independentGroupIds = currentSpecial?.groups ?? [];

  const ids = [...dependentGroupIds, ...independentGroupIds];

  const cmsDiscountsItems = promoCarousel?.carousel_items;
  const cmsDiscountItem = cmsDiscountsItems?.find(
    (item: PromoCarouselDocumentDataCarouselItemsItem) =>
      item.pos_title_name === currentSpecial?.title,
  );

  const { data, isLoading: isBogoLoading } = useQueries({
    queries: ids.map(id => ({
      queryKey: ['BogoProducts', id, store?.shortName],
      queryFn: () => searchProductsByGroupId({ id }),
    })),
    combine: results => ({
      data: results.reduce((result, current, index) => {
        const actives = current?.data; // we moved the active filter to inside the searchProductsByGroupId

        if (actives?.length) {
          if (index > dependentGroupIds.length - 1) {
            return {
              ...result,
              [`dependent-${index}`]: actives,
            };
          }

          return {
            ...result,
            [`independent-${index}`]: actives,
          };
        }

        return result;
      }, {}),
      isLoading: results.some(result => result.isPending),
    }),
  });

  const {
    bundleProducts,
    dependentProducts,
    independentProducts,
    initDepProducts,
    initIndProducts,
    processingDependents,
    processingIndependents,
    special,
  } = useMemo(() => {
    if (isLoading || !data) {
      return {
        bundleProducts: [],
        dependentProducts: [],
        independentProducts: [],
        processingDependents: false,
        processingIndependents: false,
        special: undefined,
      };
    }

    const formatedSpecial = formatSpecial(
      currentBundleProducts,
      currentSpecial,
      data,
      isBogoLoading,
      cmsDiscountItem,
    );

    const accountedProducts: CartLineItem[] = currentBundleProducts.flatMap(
      product => Array(product.quantity).fill(product),
    );

    const independentProductsAlreadyInCart = accountedProducts.filter(
      p => p?.independent,
    );

    const stillProcessingIndependent =
      independentProductsAlreadyInCart.length <
      formatedSpecial?.independentProductsRequired;

    const { dependentProductsInCart, independentProductsInCart } =
      accountedProducts.reduce(
        (result, current) => {
          // this are the products ids return by the group API call off
          // all dependent and independent products
          const depProducts = formatedSpecial?.depProducts;
          const indProducts = formatedSpecial?.indProducts;
          const productsInOriginalCart = products;

          let isIndependent = !!current?.independent;
          const prodInIndList = find(indProducts, d => d?.id === current?.id);

          if (!isIndependent && prodInIndList && stillProcessingIndependent) {
            isIndependent = true;
          }

          if (isIndependent) {
            const indexInCart = findIndex(
              productsInOriginalCart,
              p => p.id === current.id,
            );

            if (indexInCart !== -1) {
              result.independentProductsInCart.push({
                ...current,
                independent: true,
                dependent: false,
              });
            }
            return result;
          }

          let isDependent = !!current?.dependent;
          const prodInDepList = find(depProducts, d => d?.id === current?.id);
          if (!isDependent && prodInDepList) {
            isDependent = true;
          }

          if (isDependent) {
            const isDepProdsAvailable =
              result?.dependentProductsInCart?.length <
              formatedSpecial?.dependentProductsRequired;

            const indexInCart = findIndex(
              productsInOriginalCart,
              p => p.id === current.id,
            );

            if (isDepProdsAvailable && indexInCart !== -1) {
              result.dependentProductsInCart.push({
                ...current,
                dependent: true,
                independent: false,
              });
            }
            return result;
          }

          return result;
        },
        {
          dependentProductsInCart: [] as CartLineItem[],
          independentProductsInCart: [] as CartLineItem[],
        },
      );

    const i =
      formatedSpecial?.independentProductsRequired >
        independentProductsInCart?.length &&
      !formatedSpecial?.allowDiscountProducts;

    const isBogoSpecial = formatedSpecial?.method === 'BOGO';
    const updatedBundleComplete =
      formatedSpecial?.dependentProductsRequired ===
        dependentProductsInCart?.length &&
      formatedSpecial?.independentProductsRequired <=
        independentProductsInCart?.length;

    return {
      bundleProducts: independentProductsInCart.concat(dependentProductsInCart),
      dependentProducts: dependentProductsInCart,
      independentProducts: independentProductsInCart,
      initDepProducts: formatedSpecial?.depProducts,
      initIndProducts: formatedSpecial?.indProducts,
      processingDependents: !i,
      processingIndependents: i,
      special: {
        ...formatedSpecial,
        bundleComplete: isBogoSpecial
          ? updatedBundleComplete
          : formatedSpecial?.bundleComplete,
      },
    };
  }, [accounting, specials.length, isBogoLoading]);

  useEffect(() => {
    if (!bundleProducts.length) return;

    const newProducts = cloneDeep(products);

    bundleProducts.forEach((prod: CartLineItem) => {
      const index = findIndex(products, (p: CartLineItem) => p.id === prod.id);

      if (index !== -1) {
        newProducts[index] = prod;
      }
    });

    update(newProducts);
  }, [bundleProducts]);

  return {
    bundleProducts,
    currentBundleProducts,
    dependentProducts,
    independentProducts,
    initDepProducts: initDepProducts!,
    initIndProducts: initIndProducts!,
    isLoading: isLoading || isBogoLoading,
    processingDependents,
    processingIndependents,
    special,
  };
};

export default useBundleSpecial;
